import { CanvasCoordinator, ChunkAction, ChunkKey, ChunkMap, createChunkMap, ReadonlyChunkMap, Set2D } from "@kosmod/stew";
import { Mini, Surface, SurfaceID, TerrainCanvas, TerrainCanvasID, TerrainChunk } from "../defs";
import { TerrainSystemData, updateTerrainSystemDataChunk, updateWalkableChunk, WalkableNode } from "../components/TerrainSystems/terrain";
import { useEffect, useRef } from "@lukekaalim/act";
import { createProxyChunkMap } from "../components/Theatre/Surface";
import { ClientData, createJanitor, ID, MapND, useJeffect, useLogger } from "@kosmod/kit";
import { recalculateAllVisibility } from "./useTerrainVisibility";
import { merge } from "rxjs";

export type SurfaceData = {
  surfaceId: SurfaceID,

  // the backing canvas
  source: ReadonlyChunkMap<number>,
  sourceCanvasId: TerrainCanvasID,

  terrain: ChunkMap<TerrainSystemData | null>,
  walkable: ChunkMap<WalkableNode | null>,
  visibility: ChunkMap<number>,
}

export const useSurfaceData = (
  surfaces: Surface[],
  canvases: TerrainCanvas[],

  minis: ClientData<Mini>[],

  chunks: MapND<[canvas: TerrainCanvasID, x: number, y: number], TerrainChunk>,
  coordinator: CanvasCoordinator
) => {
  const logger = useLogger('SurfaceData')
  const surfaceData = useRef(() => new Map<SurfaceID, SurfaceData>()).get();

  useEffect(() => {
    const j = createJanitor();

    for (const surface of surfaces) {
      let data = surfaceData.get(surface.id);
      if (!data) {
        const canvas = canvases.find(c => c.surface === surface.id);
        if (!canvas)
          continue;

        data = {
          surfaceId: surface.id,
          sourceCanvasId: canvas.id,

          source: createProxyChunkMap(chunks, canvas.id),

          walkable: createChunkMap(ID('terrain_canvas'), () => null),
          visibility: createChunkMap(ID('terrain_canvas'), () => 0),
          terrain: createChunkMap(ID('terrain_canvas'), () => null),
        }

        surfaceData.set(surface.id, data);
      }
      const sourceCanvas = canvases.find(c => c.id === data.sourceCanvasId);
      if (!sourceCanvas)
        return;

      j.add(coordinator.updates.subscribe(chunks => {
        const updatedSourceChunks: ChunkKey[] = [];
        const updatedTerrainSystemChunks: ChunkKey[] = [];

        for (const [key] of chunks) {
          switch (key[0]) {
            case sourceCanvas.id:
              updatedSourceChunks.push(key);
              break;
            case data.terrain.canvasId:
              updatedTerrainSystemChunks.push(key)
              break;
          }
        }

        for (const [x, y] of  projectAxially([...updatedSourceChunks].map(k => [k[1], k[2]]))) {
          const chunk = data.terrain.gen(x, y);

          updateTerrainSystemDataChunk(data.source, sourceCanvas, chunk);
          coordinator.queue([[data.terrain.canvasId, x, y], 'update']);
        }
        for (const [, x, y] of updatedTerrainSystemChunks) {
          const chunk = data.walkable.gen(x, y);
          
          updateWalkableChunk(data.terrain, chunk);
          coordinator.queue([[data.walkable.canvasId, x, y], 'update']);
        }
        if (updatedTerrainSystemChunks.length > 0) {
              const start = performance.now();
          const changed = recalculateAllVisibility(data.terrain, data.visibility, minis.map(m => m.current), logger,)
          logger.log(`Recalculating Visibility due to updated terrain in ${performance.now() - start}ms`)
          coordinator.queueMany(changed);
        }
      }));
    
      logger.log(`Running init`)
      for (const [x, y] of projectAxially([...data.source.chunks.keys()])) {
        const chunk = data.terrain.gen(x, y);

        updateTerrainSystemDataChunk(data.source, sourceCanvas, chunk);
        coordinator.queue([[data.terrain.canvasId, x, y], 'update']);
      }
      for (const [x, y] of data.terrain.chunks.keys()) {
        const chunk = data.walkable.gen(x, y);

        updateWalkableChunk(data.terrain, chunk);
        coordinator.queue([[data.walkable.canvasId, x, y], 'update']);
      }

      let animatingUntil = 0;
      let id: null | ReturnType<typeof setInterval>;

      j.add(merge(...minis.map(m => m.change)).subscribe((m) => {
        if (m.animation && m.animation.points.length > 0) {
          const end = m.animation.start + m.animation.points.length * 400;
          if (end > animatingUntil) {
            animatingUntil = end;
            if (id)
              clearInterval(id);

            id = setInterval(() => {
              const start = performance.now();
              const changed = recalculateAllVisibility(data.terrain, data.visibility, minis.map(m => m.current), logger,)
              logger.log(`Recalculating Visibility due to mini animation in ${performance.now() - start}ms`)
              coordinator.queueMany(changed);
              coordinator.run()

              if (id && Date.now() > animatingUntil) {
                clearInterval(id);
                id = null;
              }
            }, 400)
          }
        } else {
          const start = performance.now();
          const changed = recalculateAllVisibility(data.terrain, data.visibility, minis.map(m => m.current), logger,)
          logger.log(`Recalculating Visibility due to mini in ${performance.now() - start}ms`)
          coordinator.queueMany(changed);
          coordinator.run()
        }
      }));

      const start = performance.now();
      const changed = recalculateAllVisibility(data.terrain, data.visibility, minis.map(m => m.current), logger,)
      logger.log(`Recalculating Visibility due to initialization in ${performance.now() - start}ms`);

      coordinator.queueMany([
        ...[...data.source.chunks.keys()].map(([x, y]): [ChunkKey, ChunkAction] => [[data.terrain.canvasId, x, y], 'update']),
        ...[...data.terrain.chunks.keys()].map(([x, y]): [ChunkKey, ChunkAction] => [[data.walkable.canvasId, x, y], 'update']),
        ...changed,
      ])
    }

    coordinator.run();

    return j.cleanup;
  }, [surfaces, canvases, minis])

  return surfaceData;
}

/**
 * Return a list of chunks
 * @param chunks
 * @returns 
 */
const projectAxially = (values: [number, number][]) => {
  return new Set2D(values.flatMap(([x, y]) => [
    [x + 1, y],
    [x - 1, y],
    [x, y + 1],
    [x, y - 1],
    [x, y],
  ]))
}